import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import { usePreviewMode } from "../hooks/usePreviewMode";
import { previewHeaders } from "../lib/previewAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PerspectivesLanding() {
  const { t, i18n } = useTranslation();
  const preview = usePreviewMode();
  const [items, setItems] = useState([]);
  const lang = (i18n.language || "en").split("-")[0];

  useEffect(() => {
    const url = `${API}/perspectives?lang=${lang}${preview ? "&include_drafts=true" : ""}`;
    axios.get(url, { headers: preview ? previewHeaders() : {} })
      .then(r => setItems(r.data || []))
      .catch(() => setItems([]));
  }, [lang, preview]);

  // While no perspectives are published, the landing page stays noindex — this
  // room does not open publicly until three essays exist.
  useEffect(() => {
    const meta = document.querySelector('meta[name="robots"]') || document.createElement("meta");
    meta.setAttribute("name", "robots");
    meta.setAttribute("content", items.length < 3 ? "noindex,nofollow" : "index,follow");
    if (!meta.parentNode) document.head.appendChild(meta);
    const t0 = document.title; document.title = "Perspectives — The Thriving Life 101";
    return () => { meta.setAttribute("content", "index,follow"); document.title = t0; };
  }, [items.length]);

  return (
    <div className="min-h-screen text-primary-custom" data-testid="perspectives-landing">
      <header className="max-w-4xl mx-auto px-6 md:px-12 pt-24 pb-16 text-center">
        <span className="text-[10px] font-sans tracking-[0.4em] uppercase text-[var(--accent-bronze)] font-semibold block">
          {t("perspectives.landing.eyebrow")}
        </span>
        <h1 className="mt-6 font-serif tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
          {t("perspectives.landing.title")}
        </h1>
        <p className="mt-6 font-serif text-lg md:text-xl italic text-secondary-custom leading-relaxed font-light max-w-2xl mx-auto">
          {t("perspectives.landing.intro")}
        </p>
      </header>

      <div className="max-w-6xl mx-auto px-6 md:px-12 pb-32">
        {items.length === 0 ? (
          <div className="max-w-2xl mx-auto text-center border-t border-b border-subtle-custom py-16" data-testid="perspectives-landing-empty">
            <p className="font-serif text-base md:text-lg italic text-secondary-custom leading-relaxed">
              {t("perspectives.landing.empty")}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-subtle-custom border-t border-b border-subtle-custom">
            {items.map((p) => (
              <li key={p.slug} className="py-10">
                <Link
                  to={`/perspectives/${p.slug}`}
                  data-testid={`perspective-landing-link-${p.slug}`}
                  className="group grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-start"
                >
                  <div className="md:col-span-3">
                    <span className="text-[10px] font-sans tracking-[0.3em] uppercase text-[var(--accent-bronze)] font-semibold">
                      {p.category}
                    </span>
                    {p.status === "draft" && (
                      <span className="mt-1 block text-[9px] font-sans tracking-[0.3em] uppercase text-secondary-custom/60">Draft</span>
                    )}
                  </div>
                  <div className="md:col-span-8 space-y-3">
                    <h2 className="font-serif tracking-tight text-2xl md:text-3xl text-primary-custom group-hover:text-[var(--accent-bronze)] transition-colors leading-tight">
                      {p.title}
                    </h2>
                    <p className="font-serif text-base italic text-secondary-custom leading-relaxed font-light max-w-2xl">
                      {p.subtitle}
                    </p>
                  </div>
                  <div className="md:col-span-1 flex md:justify-end pt-1">
                    <ArrowRight className="w-4 h-4 text-secondary-custom group-hover:text-[var(--accent-bronze)] group-hover:translate-x-1 transition-all" />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
